import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FiSearch, FiCornerDownLeft } from 'react-icons/fi';
import { IoClose } from 'react-icons/io5';
import { shortNodeId } from './NodeEditorChrome';
import { getNodeHeadline, getNodeSummary } from './nodeSummary';

const TYPE_LABELS = {
  basicAnswer: 'Respuesta Básica',
  conditions: 'Condiciones',
  saveAnswer: 'Guardar Respuesta',
  queryApi: 'Consulta API',
  template: 'Plantilla',
  webHook: 'WebHook',
  transferAgent: 'Transferir Agente',
  assistantAI: 'Asistente IA',
  json: 'JSON',
  tags: 'Etiquetas',
  flowsWhatsApp: 'Flujo WhatsApp',
};

const MAX_RESULTS = 40;

const normalize = (value) =>
  String(value ?? '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '');

// Los campos donde tiene sentido buscar: lo que el usuario reconoce del nodo.
const searchableText = (node) =>
  [
    node.data?.title,
    node.data?.user_response,
    node.data?.bot_response,
    node.data?.template_name,
    node.data?.url_api,
    // Resumen legible (ej. "serviceStatus = TRAVELLING"): lo mismo que se ve
    // en la tarjeta, para poder buscar por lo que el usuario tiene a la vista.
    getNodeSummary(node),
    TYPE_LABELS[node.type],
    node.type,
    node.id,
    ...(node.data?.options || []).map((o) => o.label),
  ]
    .filter(Boolean)
    .join(' ');

const NodeSearchPalette = ({ open, onClose, nodes, onPick, theme }) => {
  const [term, setTerm] = useState('');
  const [cursor, setCursor] = useState(0);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  const index = useMemo(
    () => nodes.map((n) => ({ node: n, haystack: normalize(searchableText(n)) })),
    [nodes]
  );

  const results = useMemo(() => {
    const q = normalize(term).trim();
    if (!q) return index.slice(0, MAX_RESULTS).map((i) => i.node);
    const words = q.split(/\s+/);
    return index
      .filter((i) => words.every((w) => i.haystack.includes(w)))
      .slice(0, MAX_RESULTS)
      .map((i) => i.node);
  }, [term, index]);

  useEffect(() => {
    if (!open) return;
    setTerm('');
    setCursor(0);
    const t = setTimeout(() => inputRef.current?.focus(), 30);
    return () => clearTimeout(t);
  }, [open]);

  useEffect(() => {
    setCursor(0);
  }, [term]);

  useEffect(() => {
    const el = listRef.current?.children?.[cursor];
    if (el?.scrollIntoView) el.scrollIntoView({ block: 'nearest' });
  }, [cursor]);

  if (!open) return null;

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setCursor((c) => Math.min(c + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setCursor((c) => Math.max(c - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (results[cursor]) onPick(results[cursor]);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
    }
  };

  return (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        zIndex: 950,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-start',
        paddingTop: '10vh',
        backgroundColor: 'rgba(0,0,0,0.32)',
      }}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        style={{
          width: 'min(560px, 90%)',
          maxHeight: '62vh',
          display: 'flex',
          flexDirection: 'column',
          borderRadius: '12px',
          border: `1px solid ${theme.border}`,
          backgroundColor: theme.panelBg,
          boxShadow: '0 18px 48px rgba(0,0,0,0.34)',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            padding: '12px 14px',
            borderBottom: `1px solid ${theme.border}`,
          }}
        >
          <FiSearch style={{ color: theme.textTertiary, fontSize: '17px', flexShrink: 0 }} />
          <input
            ref={inputRef}
            value={term}
            onChange={(e) => setTerm(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Buscar nodo por título, mensaje, botón, tipo o ID…"
            style={{
              flex: 1,
              border: 'none',
              outline: 'none',
              background: 'transparent',
              color: theme.textPrimary,
              fontSize: '14px',
            }}
          />
          <button
            type="button"
            onClick={onClose}
            style={{
              border: 'none',
              background: 'transparent',
              color: theme.textTertiary,
              cursor: 'pointer',
              display: 'flex',
              padding: 0,
            }}
          >
            <IoClose style={{ fontSize: '18px' }} />
          </button>
        </div>

        <div ref={listRef} style={{ overflowY: 'auto', padding: '6px' }}>
          {results.length === 0 && (
            <div
              style={{
                padding: '22px 14px',
                textAlign: 'center',
                color: theme.textSecondary,
                fontSize: '13px',
              }}
            >
              Ningún nodo coincide con “{term}”.
            </div>
          )}
          {results.map((node, i) => {
            const active = i === cursor;
            return (
              <div
                key={node.id}
                onMouseEnter={() => setCursor(i)}
                onMouseDown={(e) => {
                  e.preventDefault();
                  onPick(node);
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  padding: '9px 11px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  backgroundColor: active ? theme.inputBg : 'transparent',
                }}
              >
                <span
                  style={{
                    fontSize: '10px',
                    fontWeight: 700,
                    letterSpacing: '0.4px',
                    textTransform: 'uppercase',
                    color: theme.textTertiary,
                    minWidth: '112px',
                  }}
                >
                  {TYPE_LABELS[node.type] || node.type}
                </span>
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span
                    style={{
                      display: 'block',
                      color: theme.textPrimary,
                      fontSize: '13px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {getNodeHeadline(node) || node.id}
                  </span>
                  {/* Distingue nodos con el mismo título (ej. 35 "Respues Básica") */}
                  <span
                    style={{
                      display: 'block',
                      color: theme.textTertiary,
                      fontSize: '11px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    <span style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace' }}>
                      #{shortNodeId(node.id)}
                    </span>
                    {node.data?.user_response && ` · ${node.data.user_response}`}
                  </span>
                </span>
                {active && (
                  <FiCornerDownLeft style={{ color: theme.textTertiary, fontSize: '13px' }} />
                )}
              </div>
            );
          })}
        </div>

        <div
          style={{
            padding: '7px 14px',
            borderTop: `1px solid ${theme.border}`,
            color: theme.textTertiary,
            fontSize: '11px',
            display: 'flex',
            gap: '14px',
          }}
        >
          <span>↑↓ navegar</span>
          <span>Enter ir al nodo</span>
          <span>Esc cerrar</span>
          <span style={{ marginLeft: 'auto' }}>{results.length} resultado{results.length === 1 ? '' : 's'}</span>
        </div>
      </div>
    </div>
  );
};

export default NodeSearchPalette;
